import {
  ScorecardGoal,
  ScorecardSubGoal,
  FeedbackFullHeader,
} from 'store/interfaces';
import { SCORECARD_GOAL_TYPE_METRIC } from 'constants/scorecards';
import { AssessmentScaleGroupAlias } from 'store/interfaces/ScorecardExternalSubgoalRO';

interface ProcessedFeedback {
  feedbackGoalHeader: {
    assessmentScoreFinalized?: { name: string; alias?: string } | null;
    assessmentScoreOriginal?: { name: string; alias?: string } | null;
  };
  isSelfAssessment: boolean;
}

export const hasOnlyNaOrDontKnowFeedback = (
  subgoalFeedbacks: ProcessedFeedback[],
) => {
  if (subgoalFeedbacks.length === 0) {
    // No reviewer feedback also leaves the subgoal unassessable, so it should
    // be handled the same way as "N/A / Don't know" only feedback.
    return true;
  }

  return subgoalFeedbacks.every((feedback) => {
    const assessmentScore =
      feedback.feedbackGoalHeader.assessmentScoreFinalized ||
      feedback.feedbackGoalHeader.assessmentScoreOriginal;

    // A reviewer who left no feedback (no assessment score) is treated the
    // same as "N/A / Don't know": it is not a valid assessment.
    if (!assessmentScore) {
      return true;
    }

    return ['na', 'general_na'].includes(assessmentScore.alias || '');
  });
};

export const getSubgoalFeedbacks = (
  scorecardFeedbacks: FeedbackFullHeader[],
  goalId: number,
  subgoalId: number,
  currentUserId?: number,
): ProcessedFeedback[] =>
  scorecardFeedbacks
    .flatMap(({ feedbackGoalHeaders, ...rest }: FeedbackFullHeader) => {
      const targetGoal = feedbackGoalHeaders.find(
        ({ targetScorecardGoal }) => targetScorecardGoal.id === goalId,
      );

      if (!targetGoal) return [];

      const targetSubgoal = targetGoal.feedbackSubgoalHeaders?.find(
        ({ targetScorecardSubgoal }) => targetScorecardSubgoal.id === subgoalId,
      );

      if (!targetSubgoal) return [];

      return [
        {
          feedbackGoalHeader: {
            assessmentScoreFinalized: targetSubgoal.assessmentScoreFinalized,
            assessmentScoreOriginal: targetSubgoal.assessmentScoreOriginal,
          },
          isSelfAssessment: rest.user && rest.user.id === currentUserId,
        },
      ];
    })
    .filter((feedback: ProcessedFeedback) => !feedback.isSelfAssessment);

export const checkSubgoalHasOnlyNAFeedback = (
  scorecardData: any,
  goalId: number,
  subgoalId: number,
): boolean => {
  if (
    !scorecardData?.goals ||
    !scorecardData?.feedbacks ||
    !goalId ||
    !subgoalId
  ) {
    return false;
  }

  const goal = scorecardData.goals.find((g: ScorecardGoal) => g.id === goalId);
  // A goal with weight 0 does not impact the Total Score, and the warning is
  // only relevant for regular goals (not metric goals), so its subgoals never
  // need the "change weight to 0" warning in those cases.
  if (!goal || goal.weight === 0 || goal.type === SCORECARD_GOAL_TYPE_METRIC) {
    return false;
  }

  const subgoal = goal.subgoals.find(
    (s: ScorecardSubGoal) => s.id === subgoalId,
  );
  if (!subgoal || subgoal.weight === 0) {
    return false;
  }

  if (subgoal.assessmentScaleGroup?.alias === AssessmentScaleGroupAlias.TEXT) {
    return false;
  }

  const subgoalFeedbacks = getSubgoalFeedbacks(
    scorecardData.feedbacks,
    goalId,
    subgoalId,
    scorecardData.workRecord?.user?.id,
  );

  return hasOnlyNaOrDontKnowFeedback(subgoalFeedbacks);
};
